import { useMemo, useState } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { startOfDay, parseISO, format, eachDayOfInterval, subDays } from 'date-fns';
import type { LessonSession, TimelineMode } from './types';
import styles from './ProgressCharts.module.css';

interface ActivityChartProps {
  sessions: LessonSession[];
}

interface DailyData {
  date: string;
  displayDate: string;
  sessions: number;
}

export function ActivityChart({ sessions }: ActivityChartProps) {
  const [timelineMode, setTimelineMode] = useState<TimelineMode>('30d');

  const { chartData, maxSessions } = useMemo(() => {
    const now = new Date();
    const daysBack = timelineMode === '7d' ? 6 : timelineMode === '30d' ? 29 : 89;
    const startDate = startOfDay(subDays(now, daysBack));
    const endDate = startOfDay(now);

    // Group sessions by date
    const dailyMap = new Map<string, DailyData>();

    sessions.forEach((session) => {
      const date = startOfDay(parseISO(session.completed_at));
      if (date < startDate || date > endDate) return;

      const dateKey = format(date, 'yyyy-MM-dd');

      if (!dailyMap.has(dateKey)) {
        dailyMap.set(dateKey, {
          date: dateKey,
          displayDate: format(date, 'MMM dd'),
          sessions: 0,
        });
      }
      dailyMap.get(dateKey)!.sessions++;
    });

    // Fill in missing days with zeros
    const allDays = eachDayOfInterval({ start: startDate, end: endDate });
    const data = allDays.map((day) => {
      const dateKey = format(day, 'yyyy-MM-dd');
      return (
        dailyMap.get(dateKey) ?? {
          date: dateKey,
          displayDate: format(day, 'MMM dd'),
          sessions: 0,
        }
      );
    });

    const max = Math.max(...data.map((d) => d.sessions), 1);

    return { chartData: data, maxSessions: max };
  }, [sessions, timelineMode]);

  const totalInPeriod = chartData.reduce((sum, d) => sum + d.sessions, 0);

  return (
    <div className={styles.chartCard}>
      <div className={styles.chartHeader}>
        <div>
          <h3 className={styles.chartTitle}>Activity</h3>
          <p className={styles.chartSubtitle}>{totalInPeriod} sessions</p>
        </div>
        <select
          className={styles.timelineSelect}
          value={timelineMode}
          onChange={(e) => setTimelineMode(e.target.value as TimelineMode)}
        >
          <option value="7d">7 days</option>
          <option value="30d">30 days</option>
          <option value="90d">90 days</option>
        </select>
      </div>
      <div className={styles.chartContainer}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="activityGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" className={styles.gridLine} />
            <XAxis
              dataKey="displayDate"
              tick={{ fontSize: 11 }}
              tickLine={false}
              axisLine={false}
              interval={timelineMode === '7d' ? 0 : timelineMode === '30d' ? 6 : 14}
            />
            <YAxis
              tick={{ fontSize: 11 }}
              tickLine={false}
              axisLine={false}
              allowDecimals={false}
              domain={[0, Math.max(maxSessions, 3)]}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'var(--color-surface)',
                border: '1px solid var(--color-border)',
                borderRadius: '8px',
                fontSize: '12px',
              }}
              formatter={(value) => [value, 'Sessions']}
              labelFormatter={(label) => label}
            />
            <Area
              type="monotone"
              dataKey="sessions"
              stroke="#3b82f6"
              fill="url(#activityGradient)"
              strokeWidth={2}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
